import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { KenoDataServiceAPI } from '@API/gambyt-data-services/keno-api.ts'
import { DataFetchingState } from '@AppState/types.ts'
import { AnimationEnabled, AppSettings, AppSettingsState } from '@AppState/app-settings/types.ts'
import { store } from '@AppState/redux-store.ts'
import { AnimationHostConfig, fetchHostConfig } from '@AppState/host-config'
import Analytics, { AnalyticEvents } from '@Util/analytics'

const defaultSettings: AppSettings = {
  soundEnabled: false,
  autoPlay: true,
  gameSpeed: 1,
  animationsEnabled: [],
}

const initialState: AppSettingsState = {
  fetchState: DataFetchingState.IDLE,
  menuOpen: false,
  synced: false,
  appSettings: defaultSettings,
}

const fetchAppSettings = async () => {
  const appConfig = store.getState().appConfig
  const deviceId = appConfig.config?.clientId
  const playerId = appConfig.config?.playerId

  const remoteSettings = await KenoDataServiceAPI.fetchPreferences(deviceId, playerId)
  if (remoteSettings) {
    return remoteSettings
  } else {
    return defaultSettings
  }
}

export const fetchHostAndAppConfig = createAsyncThunk('fetchHostAndAppConfig', async () => {
  const hostConfig = await fetchHostConfig()
  const appSettings = await fetchAppSettings()

  if (!hostConfig.settings.gameAppearanceConfig.hasAnimationSelection) {
    return {
      appSettings: {
        ...appSettings,
        animationsEnabled: hostConfig.animations.map((a) => ({
          identifier: a.id,
          isEnabled: true,
        })),
      },
      hostConfig,
    }
  }

  const mergedAnimations: AnimationEnabled[] = hostConfig.animations.reduce(
    (acc: AnimationEnabled[], animation: AnimationHostConfig) => {
      const matchingAnimationSetting = appSettings.animationsEnabled.find(
        (a) => a.identifier === animation.id,
      )
      if (matchingAnimationSetting) {
        return [...acc, matchingAnimationSetting as AnimationEnabled]
      }

      return [...acc, { identifier: animation.id, isEnabled: true }]
    },
    [],
  )

  const updatedAppSettings = {
    ...appSettings,
    animationsEnabled: mergedAnimations,
  }
  return { appSettings: updatedAppSettings, hostConfig }
})

const trackSettingsChange = (appSettings: AppSettings) => {
  const activeSettings = []
  if (appSettings.autoPlay) {
    activeSettings.push('autoPlay')
  }

  if (appSettings.gameSpeed) {
    activeSettings.push('gameSpeed')
  }

  if (appSettings.soundEnabled) {
    activeSettings.push('soundEnabled')
  }

  Analytics.track(AnalyticEvents.SETTINGS_CHANGE, {
    activeSettings: activeSettings.join(','),
  })
}

export const appSettingsSlice = createSlice({
  name: 'app-settings',
  initialState,
  reducers: {
    updateAnimationsEnabled(state, action: { payload: AnimationEnabled[] }) {
      state.appSettings.animationsEnabled = action.payload

      Analytics.track(AnalyticEvents.ANIMATION_CHANGE, {
        activeAnimations: action.payload
          .filter((a) => a.isEnabled)
          .map((a) => a.identifier)
          .sort((a, b) => a.localeCompare(b))
          .join(','),
      })
    },
    updateAutoplay(state, action) {
      state.appSettings.autoPlay = action.payload
      trackSettingsChange(state.appSettings)
    },
    updateGameSpeed(state, action) {
      state.appSettings.gameSpeed = action.payload
      trackSettingsChange(state.appSettings)
    },
    updateSound(state, action) {
      state.appSettings.soundEnabled = action.payload
      trackSettingsChange(state.appSettings)
    },
    updateMenuOpen(state, action) {
      state.menuOpen = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHostAndAppConfig.fulfilled, (state: AppSettingsState, action) => {
      state.fetchState = DataFetchingState.SUCCEEDED
      state.synced = true

      state.appSettings = action.payload.appSettings as AppSettings
    })

    builder.addCase(fetchHostAndAppConfig.pending, (state) => {
      state.fetchState = DataFetchingState.PENDING
      state.synced = false
    })

    builder.addCase(fetchHostAndAppConfig.rejected, (state, action) => {
      console.error(action.error)
      state.fetchState = DataFetchingState.FAILED
      state.synced = true
      state.appSettings = defaultSettings
    })
  },
})

export const AppSettingsActions = {
  ...appSettingsSlice.actions,
}

export const AppSettingsReducer = appSettingsSlice.reducer
